import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { safeFetch } from '../../lib/api';
import { notifyToast } from '../../lib/toastStore';
import { confirmDialog } from '../../lib/confirmStore';
import {
  FileText, Plus, Search, X, Edit3, Trash2, Copy,
  CheckCircle, AlertCircle, AlertTriangle, Loader, Archive, Globe,
  Clock, Hash, File, ArrowUpDown, Filter, Sliders, LayoutGrid, Table as TableIcon
} from 'lucide-react';
import Modal from '../shared/Modal';
import TemplateEditor from './TemplateEditor';
import VariableManagement from './VariableManagement';
import { DocumentTemplate } from '../../types';
import { sanitizeTemplateRecord, searchText } from '../../lib/piiEnvelope';

interface DocumentTemplateManagerProps {
  adminEmail: string;
  /** False when the member has only view access to the doc-templates module —
   *  create/edit/publish/delete controls are hidden (server-enforced). */
  canEdit?: boolean;
}

const STATUS_BADGES: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-700 ring-1 ring-slate-200',
  published: 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200',
  archived: 'bg-amber-100 text-amber-700 ring-1 ring-amber-200',
};

const STATUS_DOT: Record<string, string> = {
  draft: 'bg-slate-400',
  published: 'bg-emerald-500',
  archived: 'bg-amber-500',
};

export default function DocumentTemplateManager({ adminEmail, canEdit = true }: DocumentTemplateManagerProps) {
  const [activeTab, setActiveTab] = useState<'templates' | 'variables'>('templates');
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'published' | 'archived'>('all');
  const [editingTemplate, setEditingTemplate] = useState<DocumentTemplate | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const showToast = (type: 'success' | 'error', message: string) => {
    notifyToast(type, message);
  };

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await safeFetch('/api/admin/document-templates');
      const items: DocumentTemplate[] = (data.templates || []).map((raw: any) => {
        // Ingest guard (piiEnvelope): the field mapping below uses
        // `|| ''`, which passes truthy non-strings (numeric codes,
        // envelope objects) straight into state, where the search
        // filter's string methods would throw on them.
        const t = sanitizeTemplateRecord(raw);
        return ({
        id: t.id || t.templateId,
        templateId: t.templateId || t.id,
        code: t.code || '',
        name: t.name || '',
        description: t.description || '',
        category: t.category || '',
        status: t.status || 'draft',
        version: t.version || 1,
        paperSize: t.paperSize || 'A4',
        orientation: t.orientation || 'portrait',
        margins: t.margins || { top: 20, right: 20, bottom: 20, left: 20 },
        htmlContent: t.htmlContent || '',
        css: t.css || '',
        createdBy: t.createdBy || '',
        createdByName: t.createdByName || '',
        createdAt: t.createdAt || '',
        updatedAt: t.updatedAt || '',
        updatedBy: t.updatedBy || '',
        storagePath: t.storagePath || '',
        });
      });
      setTemplates(items);
    } catch (e: any) {
      console.error('Failed to fetch templates:', e);
      showToast('error', 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleCreate = () => {
    const now = new Date().toISOString();
    setEditingTemplate({
      id: '',
      templateId: '',
      name: '',
      description: '',
      category: '',
      status: 'draft',
      version: 1,
      paperSize: 'A4',
      orientation: 'portrait',
      margins: { top: 20, right: 20, bottom: 20, left: 20 },
      htmlContent: `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: 'Helvetica', Arial, sans-serif; font-size: 12pt; line-height: 1.6; color: #333; margin: 0; padding: 0; }
    .header { text-align: center; margin-bottom: 30px; }
    .content { margin-bottom: 20px; }
    .footer { text-align: center; font-size: 9pt; color: #999; margin-top: 40px; }
    table { width: 100%; border-collapse: collapse; margin: 15px 0; }
    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
    th { background: #f5f5f5; }
    .page-break { page-break-before: always; }
  </style>
</head>
<body>
  <div class="header">
    <h1>{{programName}}</h1>
    <p>Prepared for: {{firstName}} {{lastName}}</p>
  </div>
  <div class="content">
    <p>Dear {{firstName}} {{lastName}},</p>
    <p>This document confirms your participation in the {{programName}} program.</p>
    <table>
      <tr><th>Field</th><th>Details</th></tr>
      <tr><td>Full Name</td><td>{{fullName}}</td></tr>
      <tr><td>Email</td><td>{{email}}</td></tr>
      <tr><td>Passport Number</td><td>{{passportNumber}}</td></tr>
      <tr><td>Date of Birth</td><td>{{dateOfBirth}}</td></tr>
      <tr><td>Program</td><td>{{programName}}</td></tr>
    </table>
  </div>
  <div class="footer">
    <p>Generated on {{today}} | ISATS Portal</p>
  </div>
</body>
</html>`,
      css: '',
      createdBy: '',
      createdByName: '',
      createdAt: now,
      updatedAt: now,
      updatedBy: adminEmail,
      storagePath: '',
    });
    setShowEditor(true);
  };

  const handleEdit = (template: DocumentTemplate) => {
    setEditingTemplate({ ...template });
    setShowEditor(true);
  };

  const handleDuplicate = async (template: DocumentTemplate) => {
    if (!(await confirmDialog(`Duplicate the document template "${template.name}"? A new draft template named "${template.name} (Copy)" will be created.`))) return;
    try {
      setSaving(true);
      await safeFetch('/api/admin/document-templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${template.name} (Copy)`,
          description: template.description,
          category: template.category,
          status: 'draft',
          paperSize: template.paperSize,
          orientation: template.orientation,
          margins: template.margins,
          htmlContent: template.htmlContent,
          css: template.css,
          createdByName: adminEmail,
          updatedBy: adminEmail,
        }),
      });
      showToast('success', 'Template duplicated');
      fetchTemplates();
    } catch (e: any) {
      showToast('error', 'Failed to duplicate template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setSaving(true);
      await safeFetch(`/api/admin/document-templates/${id}`, { method: 'DELETE' });
      showToast('success', 'Template deleted');
      setShowDeleteConfirm(null);
      fetchTemplates();
    } catch (e: any) {
      showToast('error', 'Failed to delete template');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (template: DocumentTemplate, newStatus: string) => {
    const statusCopy = newStatus === 'published'
      ? `Publish the document template "${template.name}"? It will become available to use for generated documents.`
      : newStatus === 'archived'
        ? `Archive the document template "${template.name}"? It will be removed from the active template list.`
        : `Change the status of the document template "${template.name}" to ${newStatus}?`;
    if (!(await confirmDialog(statusCopy))) return;
    try {
      const now = new Date().toISOString();
      await safeFetch(`/api/admin/document-templates/${template.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, updatedAt: now, updatedBy: adminEmail }),
      });
      showToast('success', `Template ${newStatus}`);
      fetchTemplates();
    } catch (e: any) {
      // Surface the server's message (e.g. "Template not found") when present.
      showToast('error', e?.message || `Failed to ${newStatus} template`);
    }
  };

  const handleSave = async (updated: DocumentTemplate) => {
    try {
      setSaving(true);
      if (updated.id) {
        const old = templates.find(t => t.id === updated.id);
        const newVersion = (old?.version || 0) + 1;

        await safeFetch(`/api/admin/document-templates/${updated.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: updated.name,
            description: updated.description,
            category: updated.category,
            paperSize: updated.paperSize,
            orientation: updated.orientation,
            margins: updated.margins,
            htmlContent: updated.htmlContent,
            css: updated.css,
            updatedBy: adminEmail,
          }),
        });

        showToast('success', 'Template updated (v' + newVersion + ')');
      } else {
        await safeFetch('/api/admin/document-templates', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: updated.name,
            description: updated.description,
            category: updated.category,
            status: 'draft',
            paperSize: updated.paperSize,
            orientation: updated.orientation,
            margins: updated.margins,
            htmlContent: updated.htmlContent,
            css: updated.css,
            createdByName: adminEmail,
            updatedBy: adminEmail,
          }),
        });

        showToast('success', 'Template created');
      }

      setShowEditor(false);
      setEditingTemplate(null);
      fetchTemplates();
    } catch (e: any) {
      showToast('error', 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(() => {
    let list = templates.filter(t =>
      searchText(t.name).toLowerCase().includes(search.toLowerCase()) ||
      searchText(t.description).toLowerCase().includes(search.toLowerCase()) ||
      searchText(t.category).toLowerCase().includes(search.toLowerCase()) ||
      searchText(t.code).toLowerCase().includes(search.toLowerCase())
    );
    if (statusFilter !== 'all') {
      list = list.filter(t => t.status === statusFilter);
    }
    return list;
  }, [templates, search, statusFilter]);

  const stats = useMemo(() => {
    const total = templates.length;
    const published = templates.filter(t => t.status === 'published').length;
    const draft = templates.filter(t => t.status === 'draft').length;
    const archived = templates.filter(t => t.status === 'archived').length;
    return { total, published, draft, archived };
  }, [templates]);

  return (
    <div className="space-y-6">
      {/* Navigation Bar / Tabs */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-3">
        <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-xl">
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-4 py-2 text-xs font-bold rounded-lg transition-all flex items-center gap-2 cursor-pointer ${
              activeTab === 'templates'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <FileText className="h-4 w-4" />
            Document Templates
          </button>
          <button
            onClick={() => setActiveTab('variables')}
            className={`px-4 py-2 text-xs font-bold rounded-lg transition-all flex items-center gap-2 cursor-pointer ${
              activeTab === 'variables'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Sliders className="h-4 w-4" />
            Variable Registry
          </button>
        </div>

        {activeTab === 'templates' && canEdit && (
          <button
            onClick={handleCreate}
            className="px-4 py-2.5 btn-primary text-white text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center gap-2 shadow-sm"
          >
            <Plus className="h-4 w-4" />
            New Template
          </button>
        )}
      </div>

      {activeTab === 'variables' ? (
        <VariableManagement />
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Total Templates', value: stats.total, icon: FileText, color: 'text-brand', bg: 'bg-blue-50' },
              { label: 'Published', value: stats.published, icon: Globe, color: 'text-emerald-600', bg: 'bg-emerald-50' },
              { label: 'Drafts', value: stats.draft, icon: File, color: 'text-slate-600', bg: 'bg-slate-100' },
              { label: 'Archived', value: stats.archived, icon: Archive, color: 'text-amber-600', bg: 'bg-amber-50' },
            ].map((stat) => (
              <div key={stat.label} className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3 hover:shadow-md transition-all">
                <div className={`h-10 w-10 rounded-xl ${stat.bg} flex items-center justify-center shrink-0`}>
                  <stat.icon className={`h-5 w-5 ${stat.color}`} />
                </div>
                <div>
                  <p className="text-xl font-extrabold text-slate-900">{stat.value}</p>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Search & Filters */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-1 w-full max-w-lg">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search templates by code, name, description, or category..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 shadow-xs"
                />
                {search && (
                  <button aria-label="Clear search" title="Clear search"
                    onClick={() => setSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              <div className="flex items-center bg-slate-100 p-1 rounded-xl shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
                    viewMode === 'table' ? 'bg-white text-slate-900 shadow-2xs' : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  <TableIcon className="h-3.5 w-3.5" />
                  <span>Table</span>
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
                    viewMode === 'grid' ? 'bg-white text-slate-900 shadow-2xs' : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  <LayoutGrid className="h-3.5 w-3.5" />
                  <span>Grid</span>
                </button>
              </div>
            </div>

            <div className="flex items-center gap-1.5 shrink-0">
              <Filter className="h-3.5 w-3.5 text-slate-400" />
              {(['all', 'draft', 'published', 'archived'] as const).map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-3 py-1.5 text-[10px] font-bold rounded-lg transition-all cursor-pointer capitalize flex items-center gap-1.5 ${
                    statusFilter === status
                      ? 'bg-brand text-white shadow-sm'
                      : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${status === 'all' ? 'bg-slate-400' : STATUS_DOT[status] || 'bg-slate-400'}`} />
                  {status}
                  {status !== 'all' && (
                    <span className="text-[11px] opacity-70">
                      {templates.filter(t => t.status === status).length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Template List */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <Loader className="h-8 w-8 text-slate-300 animate-spin" />
              <p className="text-sm font-semibold text-slate-400">Loading templates…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-slate-200">
              <div className="h-16 w-16 rounded-full bg-slate-50 flex items-center justify-center mx-auto mb-4">
                <FileText className="h-8 w-8 text-slate-200" />
              </div>
              <p className="text-sm font-extrabold text-slate-500">No templates found</p>
              <p className="text-caption text-slate-400 mt-1 max-w-xs mx-auto">
                {search || statusFilter !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'Create your first document template to get started'}
              </p>
              {(search || statusFilter !== 'all') && (
                <button
                  onClick={() => { setSearch(''); setStatusFilter('all'); }}
                  className="mt-4 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-all cursor-pointer"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : viewMode === 'table' ? (
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-2xs">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider font-extrabold border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3">Code</th>
                      <th className="px-4 py-3">Template Name</th>
                      <th className="px-4 py-3">Category</th>
                      <th className="px-4 py-3">Paper / Orientation</th>
                      <th className="px-4 py-3">Version</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filtered.map(t => (
                      <tr key={t.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3 font-mono font-bold text-slate-600">{t.code || '—'}</td>
                        <td className="px-4 py-3 font-extrabold text-slate-900">
                          <div>
                            <p className="font-extrabold text-slate-900">{t.name || 'Untitled'}</p>
                            {t.description && <p className="text-[11px] font-normal text-slate-500 truncate max-w-xs">{t.description}</p>}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {t.category ? (
                            <span className="inline-flex items-center px-2 py-0.5 bg-purple-50 text-purple-700 rounded text-[10px] font-bold">
                              {t.category}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-3 text-slate-600 capitalize">
                          {t.paperSize} · {t.orientation}
                        </td>
                        <td className="px-4 py-3 text-slate-500 font-mono">v{t.version}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 text-[10px] font-bold rounded-lg ${STATUS_BADGES[t.status] || ''}`}>
                            {t.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => handleEdit(t)} className="p-1.5 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer" title="Edit">
                              <Edit3 className="h-4 w-4" />
                            </button>
                            {canEdit && (
                              <button onClick={() => handleDuplicate(t)} className="p-1.5 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer" title="Duplicate">
                                <Copy className="h-4 w-4" />
                              </button>
                            )}
                            {canEdit && (
                              <button onClick={() => setShowDeleteConfirm(t.id)} className="p-1.5 text-slate-600 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer" title="Delete">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {filtered.map(t => (
                <div
                  key={t.id}
                  className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-lg hover:border-slate-300 transition-all group"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-10 w-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                        <FileText className="h-5 w-5 text-blue-500" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-extrabold text-slate-900 truncate">{t.code ? `[${t.code}] ` : ''}{t.name || 'Untitled'}</h3>
                        {t.category && (
                          <span className="inline-flex items-center px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded text-[10px] font-bold mt-0.5">
                            {t.category}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-lg shrink-0 ${STATUS_BADGES[t.status] || ''}`}>
                      {t.status}
                    </span>
                  </div>

                  {t.description && (
                    <p className="text-caption text-slate-500 line-clamp-2 mb-3">{t.description}</p>
                  )}

                  <div className="flex items-center gap-3 text-caption text-slate-400 mb-4">
                    <span className="flex items-center gap-1 bg-slate-50 px-2 py-0.5 rounded-md text-[10px] font-bold">
                      <Hash className="h-3 w-3" />
                      v{t.version}
                    </span>
                    <span className="flex items-center gap-1 bg-slate-50 px-2 py-0.5 rounded-md text-[10px] font-bold">
                      <ArrowUpDown className="h-3 w-3" />
                      {t.paperSize} · {t.orientation}
                    </span>
                    <span className="flex items-center gap-1 bg-slate-50 px-2 py-0.5 rounded-md text-[10px] font-bold">
                      <Clock className="h-3 w-3" />
                      {t.updatedAt ? new Date(t.updatedAt).toLocaleDateString() : '—'}
                    </span>
                  </div>

                  <div className="flex items-center gap-1 pt-3 border-t border-slate-100">
                    {canEdit && (
                    <>
                    <button
                      onClick={() => handleEdit(t)}
                      className="flex-1 px-3 py-1.5 text-[10px] font-bold text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1"
                    >
                      <Edit3 className="h-3 w-3" /> Edit
                    </button>
                    <button
                      onClick={() => handleDuplicate(t)}
                      title="Duplicate"
                      className="px-3 py-1.5 text-[10px] font-bold text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1"
                    >
                      <Copy className="h-3 w-3" />
                      <span className="hidden sm:inline">Copy</span>
                    </button>
                    {t.status !== 'published' ? (
                      <button
                        onClick={() => handleToggleStatus(t, 'published')}
                        title="Publish"
                        className="px-3 py-1.5 text-[10px] font-bold text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1"
                      >
                        <Globe className="h-3 w-3" />
                        <span className="hidden sm:inline">Publish</span>
                      </button>
                    ) : (
                      <button
                        onClick={() => handleToggleStatus(t, 'archived')}
                        title="Archive"
                        className="px-3 py-1.5 text-[10px] font-bold text-amber-600 hover:bg-amber-50 rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1"
                      >
                        <Archive className="h-3 w-3" />
                        <span className="hidden sm:inline">Archive</span>
                      </button>
                    )}
                    <button
                      onClick={() => setShowDeleteConfirm(t.id)}
                      title="Delete"
                      className="px-3 py-1.5 text-[10px] font-bold text-rose-600 hover:bg-rose-50 rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1"
                    >
                      <Trash2 className="h-3 w-3" />
                      <span className="hidden sm:inline">Delete</span>
                    </button>
                    </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Template Editor Modal */}
          {showEditor && editingTemplate && (
            <TemplateEditor
              template={editingTemplate}
              onSave={handleSave}
              onClose={() => { setShowEditor(false); setEditingTemplate(null); }}
              saving={saving}
              adminEmail={adminEmail}
            />
          )}

          {/* Delete Confirmation */}
          {showDeleteConfirm && (
            <Modal onClose={() => setShowDeleteConfirm(null)}>
              <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full mx-4">
                <div className="text-center space-y-3">
                  <div className="h-12 w-12 rounded-full bg-rose-100 flex items-center justify-center mx-auto">
                    <AlertTriangle className="h-6 w-6 text-rose-600" />
                  </div>
                  <h3 className="text-sm font-extrabold text-slate-900">Delete Template?</h3>
                  <p className="text-xs text-slate-500">This action cannot be undone. All version history will be lost.</p>
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => setShowDeleteConfirm(null)}
                      className="flex-1 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-all cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleDelete(showDeleteConfirm)}
                      disabled={saving}
                      className="flex-1 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl transition-all cursor-pointer disabled:opacity-50 flex items-center justify-center gap-1"
                    >
                      {saving ? <Loader className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </Modal>
          )}
        </>
      )}
    </div>
  );
}
